import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import AdminLayout, { adminHeaders, useAdminAuthed } from "@/components/AdminLayout";
import { Save, RotateCcw, AlertTriangle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const rupees = (paise) =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format((paise || 0) / 100);

const StatusChip = ({ label, tone }) => (
  <span className={
    "text-[11px] px-2 py-0.5 rounded-full border " +
    (tone === "danger" ? "border-red-200 bg-red-50 text-red-700"
      : tone === "warn" ? "border-amber-200 bg-amber-50 text-amber-800"
      : "border-emerald-200 bg-emerald-50 text-emerald-800")
  }>{label}</span>
);

const AdminInventoryPage = () => {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState({});
  const pw = useAdminAuthed();

  const load = async () => {
    if (!pw) return;
    setBusy(true);
    try {
      const { data } = await axios.get(`${API}/admin/inventory`, { headers: adminHeaders() });
      setRows(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load inventory");
    } finally {
      setBusy(false);
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [pw]);

  const commit = async (slug) => {
    const val = draft[slug];
    if (val === undefined || val === "") return;
    try {
      await axios.post(`${API}/admin/inventory/adjust`, { slug, set_total: Number(val), reason: "manual-set" }, { headers: adminHeaders() });
      toast.success("Stock updated");
      setDraft((d) => { const n = { ...d }; delete n[slug]; return n; });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
  };

  const adjust = async (slug, delta) => {
    try {
      await axios.post(`${API}/admin/inventory/adjust`, { slug, delta_total: delta, reason: `adjust:${delta}` }, { headers: adminHeaders() });
      toast.success(`Stock ${delta > 0 ? "added" : "removed"}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
  };

  const totals = rows.reduce((acc, r) => {
    acc.total += r.total_stock; acc.avail += r.available; acc.value += (r.total_stock * (r.price || 0));
    return acc;
  }, { total: 0, avail: 0, value: 0 });

  return (
    <AdminLayout title="Inventory" subtitle="Live stock, reserved, sold, damaged & auto out-of-stock rules">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <div className="hk-card p-4"><p className="hk-eyebrow">Products</p><p className="hk-h3 mt-1">{rows.length}</p></div>
        <div className="hk-card p-4"><p className="hk-eyebrow">Total Units</p><p className="hk-h3 mt-1">{totals.total}</p></div>
        <div className="hk-card p-4"><p className="hk-eyebrow">Available Units</p><p className="hk-h3 mt-1">{totals.avail}</p></div>
        <div className="hk-card p-4"><p className="hk-eyebrow">Inventory Value</p><p className="hk-h3 mt-1">{rupees(totals.value)}</p></div>
      </div>

      <div className="hk-card overflow-hidden">
        <table className="w-full text-[13px]">
          <thead className="bg-[var(--hk-warm-ivory)] text-[11px] uppercase tracking-widest text-[var(--hk-ink-2)]">
            <tr>
              <th className="text-left px-4 py-3 font-medium">Product</th>
              <th className="text-left px-4 py-3 font-medium">Total</th>
              <th className="text-left px-4 py-3 font-medium">Reserved</th>
              <th className="text-left px-4 py-3 font-medium">Sold</th>
              <th className="text-left px-4 py-3 font-medium">Available</th>
              <th className="text-left px-4 py-3 font-medium">Status</th>
              <th className="text-left px-4 py-3 font-medium">Set / Adjust</th>
            </tr>
          </thead>
          <tbody>
            {busy && <tr><td colSpan={7} className="px-4 py-10 text-center text-[var(--hk-ink-2)]">Loading…</td></tr>}
            {!busy && rows.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-10 text-center text-[var(--hk-ink-2)]">
                No products found. Add products under <em>Products</em> first, then set their stock here.
              </td></tr>
            )}
            {!busy && rows.map((r) => {
              const low = r.available > 0 && r.available <= (r.low_stock_threshold || 10);
              const out = r.available <= 0 && r.total_stock > 0;
              const noInv = r.total_stock === 0;
              return (
                <tr key={r.slug} className="border-t border-[var(--hk-border)]" data-testid={`inv-row-${r.slug}`}>
                  <td className="px-4 py-3">
                    <p className="font-medium text-[var(--hk-ink)]">{r.name}</p>
                    <p className="text-[11px] text-[var(--hk-ink-2)] font-mono">{r.slug}</p>
                  </td>
                  <td className="px-4 py-3">{r.total_stock}</td>
                  <td className="px-4 py-3">{r.reserved}</td>
                  <td className="px-4 py-3">{r.sold}</td>
                  <td className="px-4 py-3 font-medium">{r.available}</td>
                  <td className="px-4 py-3">
                    {out ? <StatusChip label="Out of stock" tone="danger" />
                      : low ? <StatusChip label="Low stock" tone="warn" />
                      : noInv ? <span className="text-[11px] text-[var(--hk-ink-2)]">Not tracked</span>
                      : <StatusChip label="In stock" />}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1.5">
                      <button onClick={() => adjust(r.slug, -1)} className="w-7 h-7 rounded border border-[var(--hk-border)]" data-testid={`inv-dec-${r.slug}`}>−</button>
                      <input
                        type="number"
                        placeholder={String(r.total_stock)}
                        value={draft[r.slug] ?? ""}
                        onChange={(e) => setDraft((d) => ({ ...d, [r.slug]: e.target.value }))}
                        onBlur={() => commit(r.slug)}
                        onKeyDown={(e) => { if (e.key === "Enter") commit(r.slug); }}
                        className="w-20 h-8 px-2 rounded border border-[var(--hk-border)] bg-white/70 text-center"
                        data-testid={`inv-set-${r.slug}`}
                      />
                      <button onClick={() => adjust(r.slug, +1)} className="w-7 h-7 rounded border border-[var(--hk-border)]" data-testid={`inv-inc-${r.slug}`}>+</button>
                      <button onClick={() => adjust(r.slug, +10)} className="text-[11px] px-2 py-1 rounded border border-[var(--hk-border)] ml-1" data-testid={`inv-add10-${r.slug}`}>+10</button>
                      <button onClick={() => adjust(r.slug, +100)} className="text-[11px] px-2 py-1 rounded border border-[var(--hk-border)]" data-testid={`inv-add100-${r.slug}`}>+100</button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="text-[11px] text-[var(--hk-ink-2)] mt-3 flex items-center gap-1.5">
        <AlertTriangle size={12} /> When available reaches 0, the product is automatically marked <b>Out of Stock</b> on the storefront and checkout is disabled.
      </p>
    </AdminLayout>
  );
};

export default AdminInventoryPage;
